import {
  WSList,
  WSModal,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IAttributeDefinition,
  IWorkDefinition,
  WSDataTypes
} from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo } from "react";
import { DataView } from "../../../../shared/components/DataView";

export interface ModalWorkDefinitionDetailsProps {
  workDefinition: IWorkDefinition;
  onClose: () => void;
}

const getFieldTypeLabel = (type: WSDataTypes): string => {
  const labels: Record<WSDataTypes, string> = {
    [WSDataTypes.Boolean]: "Yes/No",
    [WSDataTypes.String]: "Text",
    [WSDataTypes.Number]: "Number",
    [WSDataTypes.Datetime]: "Date/time",
    [WSDataTypes.ValueSet]: "Selection"
  };
  return labels[type] || type;
};

const getValidationText = (field: IAttributeDefinition): string => {
  const rules = field.validationRules;
  if (!rules) return "None";

  const parts: string[] = [];

  if (rules.minimum !== undefined) {
    parts.push(`Min: ${rules.minimum}`);
  }
  if (rules.maximum !== undefined) {
    parts.push(`Max: ${rules.maximum}`);
  }
  if (rules.regex) {
    parts.push(`Pattern: ${rules.regex}`);
  }
  if (rules.enumConfig) {
    if (
      "acceptedValues" in rules.enumConfig &&
      rules.enumConfig.acceptedValues
    ) {
      parts.push(`Options: ${rules.enumConfig.acceptedValues.length}`);
    } else if ("resourceType" in rules.enumConfig) {
      parts.push("Dynamic options");
    }
  }

  return parts.length > 0 ? parts.join(", ") : "None";
};

export const ModalWorkDefinitionDetails: React.FC<
  ModalWorkDefinitionDetailsProps
> = ({ workDefinition, onClose }) => {
  const tableData = useMemo(() => {
    return workDefinition.attributeDefinitions.map((field, index) => ({
      id: field.key || `field-${index}`,
      data: field
    }));
  }, [workDefinition.attributeDefinitions]);

  const columns: Array<WSTableColumn<IAttributeDefinition>> = [
    {
      config: {
        header: {
          text: "Label"
        }
      },
      renderFunction: rowItem => <WSTableCell text={rowItem.data.name} />
    },
    {
      config: {
        header: {
          text: "Type"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: rowItem => (
        <WSTableCell text={getFieldTypeLabel(rowItem.data.type)} />
      )
    },
    {
      config: {
        header: {
          text: "Required"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: rowItem =>
        rowItem.data.required ? (
          <WSTableCell text="Yes" />
        ) : (
          <WSTableCell text="No" secondaryText="Optional" />
        )
    },
    {
      config: {
        header: {
          text: "Validation"
        },
        hideOnScreens: ["XS", "S"]
      },
      renderFunction: rowItem => (
        <WSTableCell text={getValidationText(rowItem.data)} />
      )
    }
  ];

  return (
    <WSModal
      title="Work type details"
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Secondary",
            onClick: onClose,
            label: "Done"
          }
        ]
      }}
      size="M"
      onClose={onClose}
    >
      <WSList gap="L">
        <DataView
          data={[
            {
              label: "Work type",
              value: workDefinition.name
            }
          ]}
        />

        <WSText.ParagraphSm weight="medium" color="gray600">
          Field configuration
        </WSText.ParagraphSm>

        {tableData.length > 0 ? (
          <WSTable<IAttributeDefinition>
            tableData={tableData}
            columns={columns}
            data-testid="workDefinitionFieldsTable"
          />
        ) : (
          <WSText.ParagraphSm color="gray500">
            No fields configured
          </WSText.ParagraphSm>
        )}
      </WSList>
    </WSModal>
  );
};
